import { useCallback, useEffect, useRef } from "react";

import WardInfinityLogo from "../../../assets/img/HomePage/ward_infinity_logo.jpg";
import AiriwheelsLogo from "../../../assets/img/HomePage/AiriWheelsIcon.png";
import StartupVALogo from "../../../assets/img/HomePage/StartupVAlogo.png";
import CorgiInsureLogo from "../../../assets/img/HomePage/corgi_insure.png";
import ProjectWLogo from "../../../assets/img/HomePage/project_w_logo.jpg";
import RAMWLogo from "../../../assets/img/HomePage/ramw_logo.jpg";
import DCCBELogo from "../../../assets/img/HomePage/dc_cbe_logo.jpg";
import MorganEDACLogo from "../../../assets/img/HomePage/morgan_edac_logo.jpg";
import DOLLogo from "../../../assets/img/HomePage/dol_logo.jpg";
import BangNYou from "../../../assets/img/HomePage/bang_n_you_logo.png";
import OhMazing from "../../../assets/img/HomePage/oh_mazing_logo.png";

const HomePartners = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isPausedRef = useRef(false);
  const scrollAmountRef = useRef(0);
  const animationFrameRef = useRef<number | undefined>();
  const lastTimestampRef = useRef<number | null>(null);

  const startAnimation = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }

    lastTimestampRef.current = null;
    const speedPxPerSecond = 100; // slower, constant speed (independent of FPS)

    const animate = (timestamp: number) => {
      if (isPausedRef.current) return;

      if (lastTimestampRef.current == null) {
        lastTimestampRef.current = timestamp;
      }
      const dtSeconds = Math.min(0.05, (timestamp - lastTimestampRef.current) / 1000);
      lastTimestampRef.current = timestamp;

      scrollAmountRef.current += speedPxPerSecond * dtSeconds;
      const halfWidth = container.scrollWidth / 2;
      if (scrollAmountRef.current >= halfWidth) {
        scrollAmountRef.current -= halfWidth;
      }
      container.scrollLeft = scrollAmountRef.current;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    startAnimation();

    const handlePointerDown = () => {
      isPausedRef.current = true;
      if (animationFrameRef.current != null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      lastTimestampRef.current = null;
    };

    const handlePointerUp = () => {
      scrollAmountRef.current = container.scrollLeft;
      const halfWidth = container.scrollWidth / 2;
      if (scrollAmountRef.current >= halfWidth) {
        scrollAmountRef.current -= halfWidth;
        container.scrollLeft = scrollAmountRef.current;
      }
      isPausedRef.current = false;
      startAnimation();
    };

    container.addEventListener("pointerdown", handlePointerDown);
    container.addEventListener("pointerup", handlePointerUp);
    container.addEventListener("pointerleave", handlePointerUp);
    container.addEventListener("pointercancel", handlePointerUp);

    return () => {
      container.removeEventListener("pointerdown", handlePointerDown);
      container.removeEventListener("pointerup", handlePointerUp);
      container.removeEventListener("pointerleave", handlePointerUp);
      container.removeEventListener("pointercancel", handlePointerUp);
      if (animationFrameRef.current != null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [startAnimation]);

  type PartnerLogo = {
    logo: string;
    alt: string;
    url: string;
    /** When true, renders `alt` as a wordmark to the right of the icon (for icon-only assets). */
    showAltLabel?: boolean;
  };

  const partnerLogos: PartnerLogo[] = [
    {
      logo: AiriwheelsLogo,
      alt: "Airiwheels",
      url: "https://www.airiwheels.ai/",
      showAltLabel: true,
    },
    { logo: CorgiInsureLogo, alt: "Corgi Insure", url: "https://corgiinsure.com/" },
    { logo: WardInfinityLogo, alt: "Ward Infinity", url: "https://gce.jhu.edu/ward-infinity/" },
    { logo: ProjectWLogo, alt: "Project W", url: "https://www.dwt.com/about/project-w" },
    { logo: StartupVALogo, alt: "Startup VA", url: "https://startupvirginia.org/" },
    { logo: RAMWLogo, alt: "RAMW", url: "https://www.ramw.org/" },
    { logo: DCCBELogo, alt: "CBE", url: "https://dslbd.dc.gov/page/cbe-connect-0" },
    { logo: MorganEDACLogo, alt: "Morgan EDAC", url: "https://www.edacmorgan.com/" },
    { logo: DOLLogo, alt: "DOL", url: "https://dreamingoutloud.org/" },
    { logo: BangNYou, alt: "Bang'n You", url: "https://www.bangnyou.com/" },
    { logo: OhMazing, alt: "Oh-Mazing Food", url: "https://www.ohmazingfood.com/" },
  ];

  // Duplicate slides to create seamless loop
  const allSlides = [...partnerLogos, ...partnerLogos];

  return (
    <section className="pb-[100px]">
      {/* <h3 className="text-[36px] text-center font-raleway font-bold leading-tight text-primaryDark-0 mb-[48px]">
        <span className="text-primaryDark-400"> Trusted </span> By
      </h3> */}

      <div
        ref={containerRef}
        className="overflow-x-auto overflow-y-hidden w-full [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          touchAction: "pan-x",
        }}
      >
        <div className="flex items-center gap-[16px] w-max px-2">
          {allSlides.map((partnerLogo, i) => (
            <div
              key={i}
              className="flex-shrink-0 cursor-pointer rounded-lg border border-[#E9E9E9] p-2"
              onClick={() => window.open(partnerLogo.url, "_blank")}
            >
              {partnerLogo.showAltLabel ? (
                <div className="flex h-[120px] w-[282px] items-center justify-center gap-3 px-3">
                  <img
                    src={partnerLogo.logo}
                    alt={partnerLogo.alt}
                    className="h-[64px] w-auto object-contain"
                  />
                  <span className="font-raleway text-[22px] font-bold tracking-tight text-black/80">
                    {partnerLogo.alt}
                  </span>
                </div>
              ) : (
                <img
                  src={partnerLogo.logo}
                  alt={partnerLogo.alt}
                  className="h-[120px] w-[282px] object-contain"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomePartners;
